import { Button, Card, Layout, Modal, Select, Space, Input } from "antd";
import { useState } from "react";
import { nanoid } from "nanoid";
import { Question, QuestionType } from "../../../../hooks/api/survey";
import { QuestionPreview } from "../QuestionPreview";
import { QuestionEditor } from "../QuestionEditor";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableQuestionItem } from "./SortableQuestionItem";

const { Sider, Content } = Layout;

export function SurveyBuilder() {
  const [title, setTitle] = useState("");
  const [questions, setQuestions] = useState<Question[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newType, setNewType] = useState<QuestionType>("radio");

  const activeQuestion = questions.find((q) => q.id === activeId);

  const onDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setQuestions((prev) => {
      const oldIndex = prev.findIndex((q) => q.id === active.id);
      const newIndex = prev.findIndex((q) => q.id === over.id);

      const updated = [...prev];
      const [moved] = updated.splice(oldIndex, 1);
      updated.splice(newIndex, 0, moved);

      return updated;
    });
  };

  const addQuestion = () => {
    setQuestions((prev) => [
      ...prev,
      {
        id: nanoid(),
        type: newType,
        title: "",
        options: ["radio", "checkbox", "select"].includes(newType)
          ? []
          : undefined,
      },
    ]);
    setIsModalOpen(false);
  };

  const updateQuestion = (id: string, patch: Partial<Question>) => {
    setQuestions((prev) =>
      prev.map((q) => (q.id === id ? { ...q, ...patch } : q)),
    );
  };

  const deleteQuestion = (id: string) => {
    setQuestions((prev) => prev.filter((q) => q.id !== id));
    if (activeId === id) setActiveId(null);
  };

  return (
    <Layout style={{ minHeight: "70vh" }}>
      {/* Left */}
      <Sider width={260} style={{ background: "var(--ant-color-bg-container)", padding: 16 }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Input
            placeholder="Survey title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <Button type="primary" block onClick={() => setIsModalOpen(true)}>
            + New item
          </Button>
        </Space>
      </Sider>

      {/* Center */}
      <Content style={{ padding: 24 }}>
        <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext
            items={questions.map((q) => q.id)}
            strategy={verticalListSortingStrategy}
          >
            <Space direction="vertical" style={{ width: "100%" }} size="large">
              {questions.map((q) => (
                <SortableQuestionItem
                  key={q.id}
                  question={q}
                  active={q.id === activeId}
                  onSelect={() => setActiveId(q.id)}
                  onDelete={() => deleteQuestion(q.id)}
                />
              ))}
            </Space>
          </SortableContext>
        </DndContext>
      </Content>

      {/* Right */}
      <Sider width={360} style={{ background: "var(--ant-color-bg-container)", padding: 16 }}>
        {activeQuestion ? (
          <QuestionEditor
            question={activeQuestion}
            onChange={(patch) => updateQuestion(activeQuestion.id, patch)}
          />
        ) : (
          <div>Select a question</div>
        )}
      </Sider>

      {/* Modal */}
      <Modal
        title="Select question type"
        open={isModalOpen}
        onOk={addQuestion}
        onCancel={() => setIsModalOpen(false)}
      >
        <Select
          value={newType}
          onChange={setNewType}
          style={{ width: "100%" }}
          options={[
            { value: "radio", label: "Radio" },
            { value: "checkbox", label: "Checkbox" },
            { value: "select", label: "Select" },
            { value: "text", label: "Text" },
            { value: "number", label: "Number" },
          ]}
        />
      </Modal>
    </Layout>
  );
}
